/**
 * Service worker lifecycle helper.
 *
 * The flow:
 *   register()  → returns nothing; sets up update detection in the background.
 *   "sw:update-available" custom event fires on `window` once an updated worker
 *      finishes installing while an old one is still controlling the page.
 *      The detail is the waiting ServiceWorker instance.
 *   Caller (UpdatePrompt component) listens for the event, shows UI, and on
 *      "yes please" calls `applyUpdate(waiting)` to swap+reload.
 *
 * Why this is needed: installed PWAs (especially on iOS) hold onto the old SW
 * until every window/tab is closed. With this prompt, a single in-app tap is
 * enough — no need to delete and reinstall the home-screen icon.
 */

const UPDATE_EVENT = "sw:update-available";
const SHOULD_REGISTER_SW =
    process.env.NODE_ENV === "production" &&
    process.env.REACT_APP_ENABLE_SW !== "false";

function dispatchUpdate(waiting) {
    if (!waiting) return;
    window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: waiting }));
}

export function register() {
    if (!("serviceWorker" in navigator)) return;
    if (!SHOULD_REGISTER_SW) {
        window.addEventListener("load", async () => {
            try {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map((reg) => reg.unregister()));
                if ("caches" in window) {
                    const names = await caches.keys();
                    await Promise.all(names.map((name) => caches.delete(name)));
                }
            } catch (err) {
                console.warn("[sw] dev cleanup failed:", err);
            }
        });
        return;
    }
    window.addEventListener("load", async () => {
        try {
            const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });

            // Case A: a worker is already waiting when this page loads.
            if (reg.waiting && navigator.serviceWorker.controller) {
                dispatchUpdate(reg.waiting);
            }

            // Case B: a new SW is found while this page is alive.
            reg.addEventListener("updatefound", () => {
                const installing = reg.installing;
                if (!installing) return;
                installing.addEventListener("statechange", () => {
                    if (installing.state === "installed" && navigator.serviceWorker.controller) {
                        dispatchUpdate(installing);
                    }
                });
            });

            // Case C: when applyUpdate() runs, the waiting worker activates and
            // controllerchange fires. We then reload exactly once so the page
            // picks up the new bundle.
            let reloaded = false;
            navigator.serviceWorker.addEventListener("controllerchange", () => {
                if (reloaded) return;
                reloaded = true;
                window.location.reload();
            });

            // Poll for updates every 30 minutes — covers users who leave the PWA open all day.
            setInterval(() => { reg.update().catch(() => { /* offline is fine */ }); }, 30 * 60 * 1000);
        } catch (err) {
            console.warn("[sw] registration failed:", err);
        }
    });
}

export function applyUpdate(waiting) {
    if (!waiting) {
        window.location.reload();
        return;
    }
    // Tell the waiting SW to take over. The controllerchange listener above reloads.
    waiting.postMessage("skipWaiting");
}

export const SW_UPDATE_EVENT = UPDATE_EVENT;
